/**
 * Handles nested tables, i.e. when a DataTable is rendered inside another DataTable.
 */
import { createContext, useContext, useState, Dispatch, SetStateAction } from 'react';
import DataTable, { DataTableProps } from './DataTable';
import { SortConfig } from './types';

interface DataTableContextValue {
  hasDataTableAncestor: boolean;
  sortConfig: SortConfig;
  setSortConfig: Dispatch<SetStateAction<SortConfig>>;
}

const defaultSort: SortConfig = { column: '', direction: 'asc' };

const DataTableContext = createContext<DataTableContextValue>({
  hasDataTableAncestor: false,
  sortConfig: defaultSort,
  setSortConfig: () => {},
});

const DataTableWrapper: React.FC<DataTableProps> = props => {
  const parent = useContext(DataTableContext);
  const isNested = parent.hasDataTableAncestor;

  // Sorting is controlled by table headers
  // Nested tables share the parent’s header, so they use the parent’s sortConfig
  const [localSort, setLocalSort] = useState<SortConfig>(defaultSort);
  const sortConfig = isNested ? parent.sortConfig : localSort;
  const setSortConfig = isNested ? parent.setSortConfig : setLocalSort;

  return (
    <DataTableContext value={{ hasDataTableAncestor: true, sortConfig, setSortConfig }}>
      <DataTable
        {...props}
        isNested={isNested}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
      />
    </DataTableContext>
  );
};

export default DataTableWrapper;
